/**
 * Billing MODE — the single implicit fact the old org-billing UI never surfaced.
 * Every org is invoiced by exactly one of these shapes (mirrors the four branches
 * in the backend `generate-invoice-core.ts`, plus the IA + comp'd cases):
 *   broker-AI: managed (comp'd) · eft (flat billing-info amount) · selfserve
 *              (Stitch org subscription / pool) · seat (Σ per-user seats)
 *   IA:        iacomp (comp'd) · manual (per-license, finance-invoiced)
 *
 * The Trello #332 redesign makes this the FIRST thing an admin sees + picks, so
 * the scattered "which fields matter?" confusion goes away. Kept in its own module
 * (not the MUI-heavy page) so the derivation is unit-testable without rendering.
 */
import type { OrgBillingSummary } from '../services/api';

export type BillingMode = 'managed' | 'eft' | 'selfserve' | 'seat' | 'manual' | 'iacomp';

export interface BillingModeOrgShape {
  stack?: string;
  billingMode?: string;
  eftPayer?: boolean;
  enabledFeatures?: string[];
  features?: string[];
}

/**
 * Derive the org's current billing mode from its record. `isBrokerAdmin` is the
 * build-time flag (billing surfaces are gated on the build, matching the rest of
 * the org page); the per-mode content still reads the org's own fields.
 */
export function deriveBillingMode(
  org: BillingModeOrgShape | null | undefined,
  isBrokerAdmin: boolean,
): BillingMode {
  // The comp'd and default arms still differ per build; the EFT arm does NOT
  // (Trello #664). This used to be a single `if (!isBrokerAdmin) return …`
  // early return covering all three, which made 'eft' STRUCTURALLY UNREACHABLE
  // on the Intelligent Automation build — an IA org with eftPayer: true derived
  // 'manual', so no IA org could ever be put into (or even be seen to be in)
  // EFT mode, and the monthly cron would have found nothing to invoice.
  // Broker behaviour is unchanged in all four cases.
  if (org?.billingMode === 'managed') return isBrokerAdmin ? 'managed' : 'iacomp';
  if (org?.eftPayer === true) return 'eft';
  if (!isBrokerAdmin) return 'manual';
  // Broker orgs bill on the org-tier SELF-SERVE model (a Stitch subscription /
  // shared credit pool) — this is the default, NOT per-seat. The old code keyed
  // 'selfserve' off the ORG_CREDIT_POOL feature flag, but that flag is unreliably
  // set: most paying self-serve orgs lack it, so they fell through to 'seat' and
  // showed "Per-seat / R 0.00" despite an active subscription (Trello #332
  // follow-up — quantum-brokers R2625, tvc-wealth R1600 both hit this). Per-seat
  // is a legacy edge — still selectable, but never the auto-derived default.
  return 'selfserve';
}

/**
 * Presentation metadata per mode — label + one-line "how it's invoiced" blurb,
 * plus which build shows it (`broker: true` = broker-admin only) and whether it's
 * a SELECTABLE mode-switch chip (`selectable: false` = shown for reference/summary
 * but never offered as a switch target). The render layer filters by build AND
 * selectability.
 *
 * `seat` is `selectable: false` on purpose: broker "billing mode" is DERIVED from
 * three independent facts (managed → eftPayer → default self-serve), and per-seat
 * is never auto-derived (Trello #332 stopped defaulting to it) — there is also no
 * distinct "switch to per-seat" action (it would be identical to switch-to-self-serve
 * at the org level; members bill their own seats via the Members table). Offering it
 * as a peer switch-chip produced the "I picked Per-seat but there's no step to switch
 * it" bug — the callout promised an action the panel couldn't provide. Kept in the
 * enum + summary for backend-branch parity (generate-invoice-core.ts still emits a
 * per-seat invoice for qualifying orgs).
 */
// `selectorLabel` + `confirmEffect` power the direct-set billing-method selector
// (prod-off flag): the fuller icon-free label shown on the ToggleButton, and the
// one-line effect stated in the switch-confirm dialog. Kept HERE (not a separate
// forked map) so mode metadata has a single source of truth (abstract-before-duplicating).
// `broker` / `ia` say which BUILD offers the mode. They were a single `broker`
// boolean, i.e. "broker-only or IA-only, never both" — which stopped being true
// with Trello #664: EFT is now the one mode BOTH builds offer, because
// Intelligent Automation bills exactly the same way (a flat agreed monthly
// amount, paid by bank transfer). Two independent flags rather than a tri-state
// so a future both-builds mode is a one-word change and no call site has to
// branch on a magic value.
export const BILLING_MODE_META: Record<
  BillingMode,
  {
    label: string;
    blurb: string;
    broker: boolean;
    ia: boolean;
    selectable?: boolean;
    selectorLabel?: string;
    confirmEffect?: string;
  }
> = {
  managed: {
    label: "Comp'd", blurb: 'We pay — no charge, payment page hidden from the org.', broker: true, ia: false,
    selectorLabel: "Comp'd",
    confirmEffect: 'No charge — the payment page is hidden, the shared credit pool is enabled, and any EFT flag is cleared.',
  },
  eft: {
    label: 'EFT flat', blurb: 'Fixed monthly amount, paid by direct bank transfer.', broker: true, ia: true,
    selectorLabel: 'EFT (bank transfer)',
    confirmEffect: 'The org is invoiced a fixed monthly Tier + amount (set under Billing details) by bank transfer. Any comp’d subscription is cleared.',
  },
  selfserve: {
    label: 'Self-serve', blurb: 'Card subscription billed to the owner via Stitch.', broker: true, ia: false,
    selectorLabel: 'Self-serve (card)',
    confirmEffect: 'The owner is billed by card via Stitch and sees the in-app payment page. Any comp’d subscription or EFT flag is cleared.',
  },
  seat: { label: 'Per-seat', blurb: 'Sum of each active member’s own subscription.', broker: true, ia: false, selectable: false },
  manual: {
    label: 'Manual', blurb: 'Per-license, invoiced by finance.', broker: false, ia: true,
    selectorLabel: 'Manual (finance-invoiced)',
    confirmEffect: 'No automated invoice is raised — finance bills this org by hand. Any comp’d subscription or EFT flag is cleared.',
  },
  // Display-only, exactly as it is today: the IA comp'd panel has always been an
  // informational Alert with no switch action behind it, so offering it as a
  // settable chip would promise something the page cannot do (the same reason
  // `seat` is non-selectable on the broker build).
  iacomp: { label: "Comp'd", blurb: 'No charge.', broker: false, ia: true, selectable: false },
};

/** Modes the given build offers. The single place the two flags are read together. */
export function billingModesForBuild(isBrokerAdmin: boolean): BillingMode[] {
  return (Object.keys(BILLING_MODE_META) as BillingMode[]).filter(
    (m) =>
      (isBrokerAdmin ? BILLING_MODE_META[m].broker : BILLING_MODE_META[m].ia) &&
      BILLING_MODE_META[m].selectable !== false,
  );
}

export interface InvoicedAsOrgShape {
  monthlyAmountZAR?: number;
  tierName?: string;
  costPerLicenseZAR?: number;
}

/**
 * Longest invoice description the backend will store (Trello #670). Mirrors
 * `INVOICE_DESCRIPTION_MAX_CHARS` in
 * `legal-ai-backend/src/handlers/admin/invoices/invoice-description.ts`, which
 * trims to it on write — so the input's `maxLength` stops the admin typing text
 * that would be silently truncated after saving.
 *
 * The cap is a LAYOUT constraint: the description is one row of a single-page
 * A4 invoice, and `buildInvoicePdf` wraps it downward into the space the
 * subtotal / total / banking blocks occupy.
 */
export const INVOICE_DESCRIPTION_MAX_CHARS = 200;

/** The org fields an invoice needs before it can be generated. */
export interface InvoiceFieldsShape {
  monthlyAmountZAR?: number;
  tierName?: string;
  billingAddress?: string;
}

/**
 * The billing fields still MISSING before an invoice can be generated, as human
 * labels — empty array means "ready to generate".
 *
 * Mirrors the backend's own validation in `generate-invoice-core.ts` (which
 * throws `InvoiceBillingDataError` listing the same fields), so the button's
 * enabled state and the server's 400 can never disagree. Note the backend also
 * requires a billing NAME but falls back to displayName/name, which is always
 * present on a real org — so it is not surfaced here.
 *
 * Exists because the old UI only knew "complete / not complete" and showed a
 * generic "set them in Edit Billing Information" warning. Marcus repeatedly set
 * the tier + monthly amount (the two fields the EFT panel names) and hit a still-
 * dead Generate button, because the missing field was the billing ADDRESS and
 * nothing said so — 6 of the 11 production EFT orgs have no address (Trello #360).
 *
 * Named `missingBrokerInvoiceFields` until Trello #670. Nothing in the body was
 * ever broker-specific — the backend requires the SAME three fields of an
 * Intelligent Automation org (`generate-invoice-core.ts`'s `!isBrokerAi`
 * branch). Only the caller was: the IA build showed no warning and never
 * disabled the button, so an admin pressed Generate on an org that could not
 * possibly be invoiced and got a bare 400. Renamed rather than forked, so the
 * two builds cannot drift (general/abstract-before-duplicating.md).
 */
export function missingInvoiceFields(
  org: InvoiceFieldsShape | null | undefined,
): string[] {
  const missing: string[] = [];
  if (typeof org?.monthlyAmountZAR !== 'number' || !(org.monthlyAmountZAR > 0)) {
    missing.push('monthly amount');
  }
  if (!org?.tierName?.trim()) missing.push('subscription tier');
  if (!org?.billingAddress?.trim()) missing.push('billing address');
  return missing;
}

/** Join missing-field labels into a sentence fragment: "a and b", "a, b and c". */
export function formatMissingFields(fields: string[]): string {
  if (fields.length <= 1) return fields[0] ?? '';
  return `${fields.slice(0, -1).join(', ')} and ${fields[fields.length - 1]}`;
}

/** The `YYYY-MM` period `offset` months from `now` (mirrors the backend's
 *  `periodAtMonthOffset` in `utils/dates/period-utils.ts`). */
function periodAtMonthOffset(offset: number, now: Date): string {
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Whether an admin may raise an invoice for `period`: the CURRENT month or the
 * NEXT one.
 *
 * Next month is allowed because subscriptions bill in advance — invoicing a
 * client on 28 July is invoicing them for their AUGUST subscription, and before
 * this the Generate button was hard-gated to the current month, so the only
 * period Marcus could pick was July (Trello #360). Past months stay
 * download/send-only: they were either generated at the time or intentionally
 * skipped, and back-dating a new invoice number into a closed month is not a
 * thing we want a click away.
 */
export function isGeneratableInvoicePeriod(period: string, now: Date = new Date()): boolean {
  return period === periodAtMonthOffset(0, now) || period === periodAtMonthOffset(1, now);
}

/**
 * Whether `period` is the NEXT month — used to tag that row "Upcoming" in the
 * invoice table, so a period that has not started yet is self-explanatory.
 * Shares `periodAtMonthOffset` with `isGeneratableInvoicePeriod` so the two can
 * never disagree about which month is "next".
 */
export function isNextMonthPeriod(period: string, now: Date = new Date()): boolean {
  return period === periodAtMonthOffset(1, now);
}

/** The org's live subscription signal — the subset of the `OrgBillingSummary` the
 *  page already fetches that drives the self-serve "invoiced as" line (so a paying
 *  org shows its real price instead of the misleading per-seat R0). A `Partial
 *  Pick` of the canonical type: no field-list duplication, and a full
 *  `OrgBillingSummary` (or a partial, e.g. a trial with no price) is assignable.
 *  `import type` keeps this module runtime-pure + unit-testable without the page. */
export type BillingSummarySignal = Partial<
  Pick<OrgBillingSummary, 'billingStatus' | 'priceZAR' | 'packageLabel'>
>;

/**
 * One-line "how the next invoice is built" summary for a billing mode — the live
 * line the redesign shows at the top of the Billing & Plan card. Pure (given a
 * mode + org fields + member count → a string) so it's unit-testable without
 * rendering the page; the companion to `deriveBillingMode`.
 */
export function buildInvoicedAsSummary(
  mode: BillingMode,
  org: InvoicedAsOrgShape | null | undefined,
  memberCount: number,
  billing?: BillingSummarySignal | null,
): string {
  switch (mode) {
    case 'managed':
    case 'iacomp':
      return "No charge — this org is comp'd.";
    case 'eft': {
      const amt = typeof org?.monthlyAmountZAR === 'number' ? `R ${org.monthlyAmountZAR.toFixed(2)}` : 'R —';
      return `${amt} / month · ${org?.tierName?.trim() || 'no tier set'} — flat EFT invoice.`;
    }
    case 'selfserve': {
      // Drive off the live subscription, NOT the pool flag — a paying org must
      // show its real price, not the old misleading per-seat R0 (Trello #332).
      if (
        billing?.billingStatus === 'paid' &&
        typeof billing?.priceZAR === 'number' &&
        billing.priceZAR > 0
      ) {
        const pkg = billing.packageLabel ? ` · ${billing.packageLabel}` : '';
        return `R ${billing.priceZAR.toFixed(2)} / month${pkg} — self-serve subscription, billed to the owner via Stitch.`;
      }
      if (billing?.billingStatus === 'trial') {
        return 'On trial — no charge yet. Becomes a self-serve subscription (billed via Stitch) once the owner subscribes.';
      }
      return 'Self-serve — billed to the owner via Stitch once they subscribe; no active subscription yet.';
    }
    case 'seat':
      return 'Sum of active member subscriptions (0 active seats → R 0.00).';
    case 'manual': {
      if (typeof org?.costPerLicenseZAR === 'number' && memberCount > 0) {
        const total = org.costPerLicenseZAR * memberCount;
        return `R ${org.costPerLicenseZAR.toFixed(2)} × ${memberCount} licenses = R ${total.toFixed(2)} / month.`;
      }
      return 'Per-license — set the cost per license to compute the monthly total.';
    }
    default:
      return '';
  }
}
